import PDPAccordionSection from '@components/product/PDPAccordionSection';

/**
 * DecodeStepsSection — "show the working" for the /product-finder page.
 *
 * The decoder engine returns a segments[] array where every entry is a
 * slice of the item code paired with the lookup that resolved it. This
 * component surfaces that chain so a stakeholder can see the decoder is
 * a rule-based engine applying discrete lookups — not a PIM row fetch.
 *
 * Two visual layers, in this order:
 *   1. Segmented code visual — numbered brackets over each slice, arrow
 *      down to the resolved value. Mirrors the "part no. keys" posters
 *      in the Sandvik product literature (see hammer part-no. keys PDF).
 *   2. Step table — # / Slice / What it means / Lookup source / Value.
 *      One row per segment; unknown slices flagged sandvik-gold.
 *
 * Regional non-standard fallback (BG…-prefixed DTH hammers): only one
 * segment decodes, so the poster visual + table would read as empty
 * noise. Render a plain paragraph instead.
 *
 * Brand rules held: sentence-case labels, sharp corners, no font-bold
 * outside table headers, monospace only for the raw code slices,
 * carbon-black text with n5/n6 secondary, sandvik-gold for unknown.
 */
export default function DecodeStepsSection({
  segments = [],
  matchedRule,
  regionalNonStandard = false,
}) {
  if (regionalNonStandard) {
    return (
      <PDPAccordionSection title="Decode steps" defaultExpanded>
        <div
          data-testid="decode-steps-regional-fallback"
          className="px-4 py-3 text-sm text-n7 space-y-2"
        >
          <p>
            The prefix was recognised as a regional non-standard variant.
            Sandvik does not publish a code-key for the remaining
            characters of this item code, so the decoder cannot break it
            down into rule-based slices.
          </p>
          <p className="text-n5 text-xs">
            Rulebook applied: {matchedRule || '—'}
          </p>
        </div>
      </PDPAccordionSection>
    );
  }

  if (!segments || segments.length === 0) {
    return null;
  }

  return (
    <PDPAccordionSection title="Decode steps" defaultExpanded>
      <div
        data-testid="decode-steps"
        className="px-4 py-4 space-y-6"
      >
        <p className="text-sm text-n7">
          Each slice below is a piece of the item code that the decoder
          matched against a rule in the rulebook. The lookup source shows
          which table resolved the slice; the resolved value is what the
          engine emitted from that lookup.
        </p>

        <SegmentedCodeVisual segments={segments} />

        <StepTable segments={segments} matchedRule={matchedRule} />

        {matchedRule && (
          <p
            data-testid="decode-steps-rulebook-footer"
            className="text-xs text-n5"
          >
            Rulebook applied: {matchedRule}
          </p>
        )}
      </div>
    </PDPAccordionSection>
  );
}

// ---------------------------------------------------------------------------
// Poster-style segmented visual — one column per segment.
// ---------------------------------------------------------------------------

function SegmentedCodeVisual({ segments }) {
  return (
    <div
      data-testid="decode-steps-visual"
      className="flex items-start justify-start gap-4 overflow-x-auto pb-2"
    >
      {segments.map((s) => {
        const isUnknown = !!s.unknown;
        const raw = s.raw || '';
        return (
          <div
            key={`vis-${s.position}-${s.name}`}
            className="flex flex-col items-center text-center min-w-[88px]"
          >
            <span className="text-xs text-n5">{s.position}</span>
            <div
              className={`mt-1 px-3 py-2 font-mono text-sm border ${
                isUnknown
                  ? 'border-sandvik-gold-medium text-sandvik-gold-dark bg-sandvik-gold-light'
                  : 'border-carbon-black text-carbon-black bg-white'
              }`}
            >
              {raw || '—'}
            </div>
            <span
              aria-hidden="true"
              className="mt-2 text-carbon-black leading-none"
            >
              ↓
            </span>
            <span className="mt-1 text-xs text-n6 max-w-[112px]">
              {humaniseName(s.name)}
            </span>
            <span
              className={`mt-1 text-sm max-w-[112px] break-words ${
                isUnknown ? 'text-sandvik-gold-dark' : 'text-carbon-black'
              }`}
            >
              {resolvedValueText(s)}
            </span>
          </div>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Four-column step table.
// ---------------------------------------------------------------------------

function StepTable({ segments, matchedRule }) {
  return (
    <div className="overflow-x-auto">
      <table
        data-testid="decode-steps-table"
        className="w-full text-sm border border-n2"
      >
        <thead className="bg-n1 text-n7">
          <tr>
            <Th className="w-10">#</Th>
            <Th className="w-24">Slice</Th>
            <Th>What it means</Th>
            <Th>Lookup source</Th>
            <Th>Resolved value</Th>
          </tr>
        </thead>
        <tbody>
          {segments.map((s) => {
            const isUnknown = !!s.unknown;
            const rowClass = isUnknown ? 'bg-sandvik-gold-light' : '';
            return (
              <tr
                key={`row-${s.position}-${s.name}`}
                className={`border-t border-n2 ${rowClass}`}
              >
                <Td className="text-n5">{s.position}</Td>
                <Td className="font-mono text-carbon-black">
                  {s.raw || '—'}
                </Td>
                <Td className="text-carbon-black">
                  {humaniseName(s.name)}
                </Td>
                <Td className="text-n6">
                  {lookupSourceText(matchedRule, s.name)}
                </Td>
                <Td
                  className={
                    isUnknown ? 'text-sandvik-gold-dark' : 'text-carbon-black'
                  }
                >
                  {resolvedValueText(s)}
                </Td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function Th({ children, className = '' }) {
  return (
    <th
      className={`text-left px-3 py-2 font-normal font-sandvik-headline ${className}`}
    >
      {children}
    </th>
  );
}

function Td({ children, className = '' }) {
  return <td className={`px-3 py-2 align-top ${className}`}>{children}</td>;
}

// ---------------------------------------------------------------------------
// Text helpers — kept local so the component is self-contained. Mirror
// the humanise / formatValue helpers in DecodedProductCard.jsx so both
// surfaces render segment names the same way.
// ---------------------------------------------------------------------------

const META_KEYS = new Set([
  'note',
  'description',
  'interpretation',
  'catalog_verified',
  'literal',
]);

function humaniseName(key) {
  if (!key) return '';
  return String(key)
    .replace(/[_-]+/g, ' ')
    .replace(/^./, (c) => c.toUpperCase());
}

function formatValue(v) {
  if (v === null || v === undefined || v === '') return '';
  if (typeof v === 'boolean') return v ? 'yes' : 'no';
  if (typeof v === 'object') return JSON.stringify(v);
  return String(v);
}

function resolvedValueText(seg) {
  if (!seg) return '—';
  if (seg.unknown) return '—';
  const dec = seg.decoded;
  if (!dec || typeof dec !== 'object') return '—';
  const entries = Object.entries(dec).filter(([k]) => !META_KEYS.has(k));
  if (entries.length === 0) {
    // Only meta keys — surface the literal / description text so the
    // row still reads (regional-non-standard main segment lands here).
    const desc = dec.description || dec.literal || dec.interpretation;
    return desc ? String(desc) : '—';
  }
  return entries
    .map(([, v]) => formatValue(v))
    .filter((s) => s !== '')
    .join(' · ');
}

function lookupSourceText(matchedRule, segmentName) {
  const rule = matchedRule ? String(matchedRule) : 'rulebook';
  if (!segmentName) return `${rule} rulebook`;
  return `${rule} rulebook · ${segmentName}`;
}
